import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Button,
  Stack,
  Fade,
  Slide,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  School as SchoolIcon,
  CheckCircle as CheckCircleIcon,
  WhatsApp as WhatsAppIcon,
  CalendarMonth as CalendarIcon,
  Group as GroupIcon,
  Assignment as AssignmentIcon,
  EmojiEvents as AwardIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../config";
import SEOHead from "../components/SEOHead";

const faqs = [
  {
    q: "如何預訂學校到校課程？",
    a: "請透過 WhatsApp (+852 8437 8839) 或聯絡表格與我們聯繫。我們會安排免費諮詢，了解學校需求後提供度身訂造的課程方案及報價。",
  },
  {
    q: "到校工作坊適合哪些年齡層？",
    a: "我們的到校課程適合小學至中學生（6–18歲），亦設有適合成人的教職員工作坊。課程內容會按年齡和程度調整，確保每位學員都能安全完成作品。",
  },
  {
    q: "每節課程大概費用是多少？",
    a: "費用視乎課程種類、班級人數及上課地點而定。我們提供彈性報價，歡迎聯絡我們索取詳細資料。學校長期合作另有優惠。",
  },
  {
    q: "學校需要準備什麼設備或材料？",
    a: "本公司提供全套材料及工具，學校只需提供課室及基本桌椅。我們負責所有準備、清潔及收尾工作，不增加教職員工作量。",
  },
  {
    q: "有哪些課程類型可供選擇？",
    a: "主要課程包括：蠟燭製作（香薰蠟燭、大豆蠟燭）、植物護理（多肉植物、苔蘚球）、及其他手工藝課程。可根據學校主題或活動需求度身訂造。",
  },
];

const steps = [
  {
    icon: <WhatsAppIcon />,
    title: "聯絡查詢",
    desc: "WhatsApp 或填寫表格，說明活動日期及人數",
  },
  {
    icon: <AssignmentIcon />,
    title: "免費諮詢",
    desc: "了解需求後提供度身訂造課程方案及報價",
  },
  {
    icon: <CalendarIcon />,
    title: "確認預訂",
    desc: "確認日期後簽署協議，我們負責所有準備",
  },
  {
    icon: <AwardIcon />,
    title: "順利開課",
    desc: "導師攜帶全套材料上門，課後完成清潔收拾",
  },
];

const highlights = [
  "無需準備材料，導師攜帶全套工具",
  "適合課後興趣班、特別活動",
  "全港上門服務，中小學適用",
  "超過 20 間學校及機構合作經驗",
];

const SchoolProgram: React.FC = () => {
  const [portfolioPdf, setPortfolioPdf] = useState<string | null>(null);
  const [portfolioPdfLoading, setPortfolioPdfLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/portfolio/pdf`)
      .then((res) => {
        if (res.data?.pdf) setPortfolioPdf(res.data.pdf);
      })
      .catch(() => {})
      .finally(() => setPortfolioPdfLoading(false));
  }, []);

  const handleDownloadPortfolio = () => {
    if (!portfolioPdf) return;
    const url = portfolioPdf.startsWith("http")
      ? portfolioPdf
      : `${API_URL}${portfolioPdf}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const structuredData = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "學校到校手工藝工作坊方案",
        description:
          "CosyLab 提供一站式學校到校課程服務，包括蠟燭製作、植物護理等手工藝工作坊。適合中小學課後興趣班、特別活動及長期駐校合作。",
        provider: {
          "@type": "EducationalOrganization",
          name: "CosyLab",
          url: "https://cosylabdesigns.com",
        },
        areaServed: { "@type": "City", name: "Hong Kong" },
        audience: {
          "@type": "EducationalAudience",
          educationalRole: "student",
          audienceType: "中小學生、學校團體",
        },
        serviceType: ["到校課程", "學校工作坊", "課後興趣班", "駐校開班"],
        url: "https://cosylabdesigns.com/school-program",
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "首頁",
            item: "https://cosylabdesigns.com/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "學校方案",
            item: "https://cosylabdesigns.com/school-program",
          },
        ],
      },
    ],
    [],
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "radial-gradient(ellipse at top left, rgba(139,195,74,0.08) 0%, transparent 50%), linear-gradient(180deg, rgba(250,251,248,1) 0%, rgba(245,248,242,1) 100%)",
      }}
    >
      <SEOHead
        title="2026 學校到校手工藝工作坊方案｜CosyLab 一站式到校課程"
        description="CosyLab 提供一站式學校到校課程：蠟燭製作、植物護理工作坊，適合中小學課後興趣班及特別活動。導師上門，免費諮詢。"
        canonical="https://cosylabdesigns.com/school-program"
        keywords="學校到校課程, 到校手工藝工作坊, 學校興趣班, 到校活動, 課後興趣班, 駐校開班, 學校工作坊香港, 中小學到校課程"
        structuredData={structuredData}
      />

      {/* Hero */}
      <Box
        component="section"
        sx={{
          pt: { xs: 8, md: 12 },
          pb: { xs: 6, md: 10 },
          position: "relative",
          zIndex: 1,
        }}
      >
        <Container maxWidth="lg">
          <Slide direction="down" in timeout={700}>
            <Box textAlign="center">
              <Chip
                icon={<SchoolIcon />}
                label="學校到校課程專頁"
                sx={{
                  mb: 3,
                  backgroundColor: "rgba(139,195,74,0.12)",
                  color: "#2D5A27",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  border: "1px solid rgba(139,195,74,0.3)",
                }}
              />
              <Typography
                variant="h1"
                component="h1"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "2rem", md: "3rem" },
                  lineHeight: 1.2,
                  background:
                    "linear-gradient(135deg, #2D5A27 0%, #8BC34A 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  mb: 3,
                }}
              >
                到校興趣班方案
              </Typography>
              <Typography
                component="p"
                sx={{
                  fontSize: { xs: "0.95rem", md: "1.05rem" },
                  color: "#4a6741",
                  fontWeight: 400,
                  maxWidth: 720,
                  mx: "auto",
                  lineHeight: 1.75,
                  mb: 5,
                }}
              >
                CosyLab
                是專為香港中小學提供一站式到校興趣班與手工藝工作坊的機構。專業導師全港上門授課，並準備全套安全材料；課程涵蓋雪山蠟燭,手捏蠟燭,瓶栽,苔玉等手作，適合課後興趣班、ECA
                聯課活動及校本特別活動。
              </Typography>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="center"
              >
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<WhatsAppIcon />}
                  href="https://wa.me/85284378839"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    background:
                      "linear-gradient(135deg, #2D5A27 0%, #8BC34A 100%)",
                    color: "white",
                    fontWeight: 700,
                    px: 4,
                    py: 1.8,
                    borderRadius: 3,
                    fontSize: "1rem",
                    boxShadow: "0 4px 20px rgba(45,90,39,0.25)",
                    "&:hover": { boxShadow: "0 6px 28px rgba(45,90,39,0.35)" },
                  }}
                >
                  WhatsApp 諮詢
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleDownloadPortfolio}
                  disabled={portfolioPdfLoading || !portfolioPdf}
                  startIcon={<DownloadIcon />}
                  sx={{
                    borderColor: "#2D5A27",
                    color: "#2D5A27",
                    fontWeight: 600,
                    px: 4,
                    py: 1.8,
                    borderRadius: 3,
                    fontSize: "1rem",
                    "&:hover": { backgroundColor: "rgba(45,90,39,0.06)" },
                  }}
                >
                  {portfolioPdfLoading
                    ? "載入中…"
                    : portfolioPdf
                      ? "下載工作坊目錄"
                      : "目錄暫不可用"}
                </Button>
              </Stack>
            </Box>
          </Slide>
        </Container>
      </Box>

      {/* Highlights */}
      <Box
        component="section"
        sx={{ py: { xs: 6, md: 8 }, backgroundColor: "rgba(139,195,74,0.05)" }}
      >
        <Container maxWidth="lg">
          <Fade in timeout={900}>
            <Box>
              <Typography
                variant="h2"
                component="h2"
                textAlign="center"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  color: "#2D5A27",
                  mb: 5,
                }}
              >
                為什麼選擇 CosyLab？
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                    md: "1fr 1fr 1fr",
                  },
                  gap: 3,
                }}
              >
                {highlights.map((h, i) => (
                  <Box
                    key={i}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                      p: 3,
                      backgroundColor: "white",
                      borderRadius: 3,
                      boxShadow: "0 2px 12px rgba(139,195,74,0.1)",
                      border: "1px solid rgba(139,195,74,0.15)",
                    }}
                  >
                    <CheckCircleIcon
                      sx={{ color: "#8BC34A", mt: 0.25, flexShrink: 0 }}
                    />
                    <Typography
                      sx={{ color: "#333", fontWeight: 500, lineHeight: 1.6 }}
                    >
                      {h}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* How it works */}
      <Box component="section" sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Fade in timeout={1000}>
            <Box>
              <Typography
                variant="h2"
                component="h2"
                textAlign="center"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  color: "#2D5A27",
                  mb: 5,
                }}
              >
                預訂流程
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr 1fr", md: "1fr 1fr 1fr 1fr" },
                  gap: 3,
                }}
              >
                {steps.map((s, i) => (
                  <Box key={i} textAlign="center">
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: "50%",
                        background:
                          "linear-gradient(135deg, #2D5A27 0%, #8BC34A 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 2,
                        color: "white",
                        boxShadow: "0 4px 16px rgba(45,90,39,0.2)",
                      }}
                    >
                      {s.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{ fontWeight: 700, color: "#2D5A27", mb: 1 }}
                    >
                      {s.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "#666", lineHeight: 1.6 }}
                    >
                      {s.desc}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Services */}
      <Box
        component="section"
        sx={{ py: { xs: 6, md: 8 }, backgroundColor: "rgba(139,195,74,0.05)" }}
      >
        <Container maxWidth="lg">
          <Fade in timeout={1000}>
            <Box>
              <Typography
                variant="h2"
                component="h2"
                textAlign="center"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  color: "#2D5A27",
                  mb: 2,
                }}
              >
                適合哪些學校活動？
              </Typography>
              <Typography
                textAlign="center"
                sx={{ color: "#666", mb: 5, maxWidth: 600, mx: "auto" }}
              >
                我們的到校課程靈活配合各類學校活動，由單次工作坊到長期興趣班均可安排
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                    md: "1fr 1fr 1fr",
                  },
                  gap: 3,
                }}
              >
                {[
                  {
                    title: "課後興趣班",
                    desc: "長期固定班，每週或每兩週一節，配合學校時間表",
                  },
                  {
                    title: "特別日活動",
                    desc: "才藝日、週會活動、家長日手作攤位等一次性活動",
                  },
                  {
                    title: "教職員培訓日",
                    desc: "老師發展日或團隊建立活動，舒壓療癒手作體驗",
                  },
                ].map((item, i) => (
                  <Box
                    key={i}
                    sx={{
                      p: 3,
                      backgroundColor: "white",
                      borderRadius: 3,
                      boxShadow: "0 2px 12px rgba(139,195,74,0.08)",
                      border: "1px solid rgba(139,195,74,0.15)",
                      transition: "transform 0.2s ease, box-shadow 0.2s ease",
                      "&:hover": {
                        transform: "translateY(-3px)",
                        boxShadow: "0 8px 24px rgba(139,195,74,0.18)",
                      },
                    }}
                  >
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{ fontWeight: 700, color: "#2D5A27", mb: 1 }}
                    >
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "#666", lineHeight: 1.6 }}
                    >
                      {item.desc}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* FAQ */}
      <Box component="section" sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="md">
          <Fade in timeout={1000}>
            <Box>
              <Typography
                variant="h2"
                component="h2"
                textAlign="center"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  color: "#2D5A27",
                  mb: 5,
                }}
              >
                常見問題
              </Typography>
              {faqs.map((faq, i) => (
                <Accordion
                  key={i}
                  disableGutters
                  elevation={0}
                  sx={{
                    mb: 1.5,
                    border: "1px solid rgba(139,195,74,0.2)",
                    borderRadius: "12px !important",
                    "&:before": { display: "none" },
                    "&.Mui-expanded": {
                      boxShadow: "0 4px 16px rgba(139,195,74,0.12)",
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: "#2D5A27" }} />}
                    sx={{ px: 3, py: 1.5 }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: "#2D5A27",
                        fontSize: "0.95rem",
                      }}
                    >
                      {faq.q}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 3, pb: 2.5 }}>
                    <Typography sx={{ color: "#555", lineHeight: 1.8 }}>
                      {faq.a}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* CTA */}
      <Box
        component="section"
        sx={{
          py: { xs: 8, md: 10 },
          background: "linear-gradient(135deg, #2D5A27 0%, #8BC34A 100%)",
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <Fade in timeout={1000}>
            <Box>
              <GroupIcon
                sx={{ fontSize: 56, color: "rgba(255,255,255,0.8)", mb: 2 }}
              />
              <Typography
                variant="h2"
                component="h2"
                sx={{
                  fontWeight: 800,
                  color: "white",
                  fontSize: { xs: "1.6rem", md: "2.2rem" },
                  mb: 2,
                }}
              >
                立即為學校查詢到校課程方案
              </Typography>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.85)",
                  mb: 5,
                  fontSize: "1.1rem",
                  maxWidth: 520,
                  mx: "auto",
                }}
              >
                免費諮詢，度身訂造課程，歡迎透過 WhatsApp 或聯絡表格與我們聯繫
              </Typography>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="center"
              >
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<WhatsAppIcon />}
                  href="https://wa.me/85284378839"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    backgroundColor: "white",
                    color: "#2D5A27",
                    fontWeight: 700,
                    px: 4,
                    py: 1.8,
                    borderRadius: 3,
                    fontSize: "1rem",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.92)" },
                  }}
                >
                  WhatsApp：8437 8839
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  component={RouterLink}
                  to="/contact"
                  sx={{
                    borderColor: "rgba(255,255,255,0.7)",
                    color: "white",
                    fontWeight: 600,
                    px: 4,
                    py: 1.8,
                    borderRadius: 3,
                    fontSize: "1rem",
                    "&:hover": {
                      borderColor: "white",
                      backgroundColor: "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  填寫聯絡表格
                </Button>
              </Stack>
              <Divider sx={{ borderColor: "rgba(255,255,255,0.2)", my: 4 }} />
              <Typography
                sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.9rem" }}
              >
                也在為企業或社區活動尋找工作坊方案？{" "}
                <Box
                  component={RouterLink}
                  to="/community-program"
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    textDecoration: "underline",
                  }}
                >
                  查看企業及社區方案 →
                </Box>
              </Typography>
            </Box>
          </Fade>
        </Container>
      </Box>
    </Box>
  );
};

export default SchoolProgram;
